<template>
    <app-layout title="Settings">
        <form @submit.prevent="submit">
            <page-header>
                <template #title>Settings - Locations</template>

                <template #actions>
                    <jet-button type="button" class="h-7 leading-none" @click="showLocationModal = 'add'" >
                        Add Location
                    </jet-button>
                </template>
            </page-header>

            <settings-navigation />

            <div class="flex px-8">
                <div class="flex gap-x-2 w-3/4">
                    <div class="flex flex-col flex-1">
                        <jet-input type="text" class="h-7" name="search" v-model="mainForm.search" @input="handleSubmit" placeholder="Search..."/>
                    </div>
                    <div class="self-end">
                        <jet-tertiary-button type="button" class="py-1 h-7" @click="handleReset" >
                            Reset
                        </jet-tertiary-button>
                    </div>
                </div>
            </div>

            <div class="px-8 mt-2">
                <div class="rounded-t overflow-hidden border border-b-0 w-3/4">
                    <table class="table w-full">
                        <thead>
                            <tr>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="name">Name</sortable-header>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="email">Email</sortable-header>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="phone">Phone</sortable-header>
                                <th>Address</th>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="type">Type</sortable-header>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="location in locations.data" :key="location.id" class="cursor-pointer" @click="editLocation(location)">
                                <td class="text-left">
                                    {{ location.name }}
                                </td>
                                <td class="text-left">
                                    {{ location.email }}
                                </td>
                                <td class="text-left">
                                    {{ location.phone }}
                                </td>
                                <td class="text-left">
                                    {{ location.street_1 }}
                                    {{ location.street_2 }}
                                    {{ location.city }}
                                    {{ location.province }}
                                    {{ location.postal }}
                                </td>
                                <td class="text-left">
                                    {{ location.type }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="flex justify-between items-center mt-4 w-3/4">
                    <pagination class="mt-0 mb-0" :links="locations.links" />

                    <div class="w-40">
                        <multiselect
                            v-model.number="mainForm.paginate"
                            :options="paginateOptions"
                            :canDeselect="false"
                            :canClear="false"
                            :classes="{ singleLabel: 'w-full px-2' }"
                            class="h-8 multi-sm multi-custom text-base"
                            openDirection="top"
                            @change="handleSubmit"
                        />
                    </div>
                </div>
            </div>

            <!-- Add / Edit location -->
            <jet-dialog-modal :show="showLocationModal" @close="closeLocationModal">
                <template #title>
                     <h3 class="text-2xl">{{ showLocationModal == 'add' ? 'Add' : 'Edit' }} Location</h3>
                </template>

                <template #content>
                    <form @submit.prevent="submitForm" class="my-6">
                        <div class="flex items-center">
                            <jet-label value="Type:" style="min-width: 60px;" />
                            <multiselect
                                v-model="form.type"
                                :options="types"
                                :closeOnSelect="true"
                                :canClear="false"
                                class="multi-lg text-base"
                            />
                            <div class="flex flex-row ml-2 items-center">
                                <toggle id="active" class="py-1" v-model="form.active" />
                                <jet-label for="active" value="Active" class="ml-2 inline-block" />
                            </div>
                        </div>
                        <div class="flex gap-x-4 items-center mt-1 mb-4">
                            <div style="min-width: 60px;" />
                            <jet-input-error :message="form.errors.type" />
                        </div>

                        <div class="mb-4">
                            <jet-label value="Name:" class="mb-0" style="min-width: 60px;" />
                            <jet-input v-model="form.name" type="text" class="w-full" />
                            <jet-input-error class="mt-1" :message="form.errors.name" />
                        </div>

                        <div class="flex items-center mb-4 justify-between">
                            <div style="width: 50%;">
                                <jet-label value="Email:" class="mb-0" />
                                <jet-input v-model="form.email" type="email" class="w-full" style="min-width: 250px;" />
                                <jet-input-error class="mt-1" :message="form.errors.email" />
                            </div>

                            <div class="ml-2" style="width: 50%;">
                                <jet-label value="Phone:" class="mb-0" />
                                <jet-input v-model="form.phone" type="text" class="w-full" style="min-width: 250px;" />
                                <jet-input-error class="mt-1" :message="form.errors.phone" />
                            </div>
                        </div>

                        <div class="flex items-center mb-4 justify-between">
                            <div style="width: 50%;">
                                <jet-label value="Street 1:" class="mb-0" />
                                <jet-input v-model="form.street_1" type="text" class="w-full" />
                                <jet-input-error class="mt-1" :message="form.errors.street_1" />
                            </div>

                            <div class="ml-2" style="width: 50%;">
                                <jet-label value="Street 2:" class="mb-0" />
                                <jet-input v-model="form.street_2" type="text" class="w-full" />
                                <jet-input-error class="mt-1" :message="form.errors.street_2" />
                            </div>
                        </div>

                        <div class="flex items-center mb-4 justify-between">
                            <div style="width: 33%;">
                                <jet-label value="City:" class="mb-0" />
                                <jet-input v-model="form.city" type="text" class="w-full" />
                                <jet-input-error class="mt-1" :message="form.errors.city" />
                            </div>

                            <div class="ml-1" style="width: 33%;">
                                <jet-label value="Province:" class="mb-0" />
                                <jet-input v-model="form.province" type="text" class="w-full" />
                                <jet-input-error class="mt-1" :message="form.errors.province" />
                            </div>

                            <div class="ml-1" style="width: 33%;">
                                <jet-label value="Postal Code:" class="mb-0" />
                                <jet-input v-model="form.postal" type="text" class="w-full" />
                                <jet-input-error class="mt-1" :message="form.errors.postal" />
                            </div>
                        </div>

                        <hr>

                        <div class="flex items-center mb-4 mt-4 justify-between">
                            <div style="width: 50%;">
                                <jet-label value="Store Code:" class="mb-0" />
                                <jet-input v-model="form.store_code" type="text" class="w-full" :disabled="showLocationModal === 'edit'" />
                                <jet-input-error class="mt-1" :message="form.errors.store_code" />
                            </div>

                            <div class="ml-1" style="width: 50%;">
                                <jet-label value="Commission Rate:" class="mb-0" />
                                <jet-input v-model="form.commission_rate" :step="0.01" type="number" class="w-full" />
                                <jet-input-error class="mt-1" :message="form.errors.commission_rate" />
                            </div>
                        </div>

                        <div class="mb-4">
                            <jet-label value="Pickup Days:" />
                            <multiselect
                                v-model="form.pickup_day"
                                :options="days"
                                :closeOnSelect="false"
                                :canClear="false"
                                class="multi-lg text-base"
                                mode="tags"
                            />
                            <jet-input-error class="mt-1" :message="form.errors.pickup_day" />
                        </div>

                        <div class="mb-4">
                            <jet-label value="Pickup Hours:" class="mb-0" style="min-width: 60px;" />
                             <div class="flex w-full">
                                <span class="px-3 border border-r-0 rounded-r-none rounded-l" style="background-color: #e9ecef; line-height: 30px;">Hours</span>
                                <jet-input v-model="form.pickup_hours" type="text" class="w-full rounded-l-none" />
                            </div>
                            <jet-input-error class="mt-1" :message="form.errors.pickup_hours" />
                        </div>

                        <input type="submit" class="hidden" ref="submitFormButton" />
                    </form>
                </template>

                <template #footer>
                    <jet-danger-button v-if="showLocationModal === 'edit'" class="mr-2" @click="showDeleteModal = true">
                        Delete
                    </jet-danger-button>

                    <jet-button class="mr-2" @click="$refs.submitFormButton.click()" :disabled="form.processing">
                        Save
                    </jet-button>

                    <jet-tertiary-button @click="closeLocationModal">
                        Cancel
                    </jet-tertiary-button>
                </template>
            </jet-dialog-modal>

            <!-- Delete Location -->
            <jet-confirmation-modal :show="showDeleteModal" @close="showDeleteModal = false">
                <template #title>
                    Delete Location
                </template>

                <template #content>
                    Are you sure you would like to delete location?
                </template>

                <template #footer>
                    <jet-danger-button @click="handleDeleteLocation">
                        Yes
                    </jet-danger-button>

                    <jet-tertiary-button class="ml-2" @click="showDeleteModal = false">
                        No
                    </jet-tertiary-button>
                </template>
            </jet-confirmation-modal>
        </form>
    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError.vue';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetButton from '@/Jetstream/Button';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import SettingsNavigation from '@/Pages/Settings/Partials/SettingsNavigation';
import Multiselect from '@vueform/multiselect';
import Pagination from '@/Jetstream/Pagination'
import SortableHeader from '@/Shared/SortableHeader';
import Toggle from '@vueform/toggle';

import '@vueform/multiselect/themes/default.css';
import '@vueform/toggle/themes/default.css';

export default {
    setup(props) {
        const mainForm = useForm({
            paginate: props.paginate,
            search: props.search,
            sortColumn: props.sortColumn,
            sortDirection: props.sortDirection,
        });

        const form = useForm({
            id: null,
            active: true,
            type: '',
            name: '',
            email: '',
            phone: '',
            street_1: '',
            street_2: '',
            city: '',
            province: '',
            postal: '',
            pickup_day: [],
            pickup_hours: '',
            commission_rate: '0.01',
            store_code: '',
        });

        return { mainForm, form }
    },

    components: {
        AppLayout,
        PageHeader,
        JetLabel,
        JetInput,
        JetInputError,
        JetDialogModal,
        JetConfirmationModal,
        JetButton,
        JetDangerButton,
        JetTertiaryButton,
        SettingsNavigation,
        Multiselect,
        Pagination,
        SortableHeader,
        Toggle,
    },

    props: [
        'locations',
        'search',
        'sortColumn',
        'sortDirection',
        'paginate',
        'types',
    ],

    data() {
        return {
            showLocationModal: false,
            showDeleteModal: false,
            days: ['monday','tuesday','wednesday','thursday','friday','saturday'],

            paginateOptions: [
                { value: 10, label: 'Show 10' },
                { value: 20, label: 'Show 20' },
                { value: 50, label: 'Show 50' },
                { value: 100, label: 'Show 100' },
            ],
        }
    },

    methods: {
        handleSubmit: _.debounce(function () {
            this.mainForm.get(route('settings.location.index'), { preserveState: true });
        }, 250),

        editLocation(data) {
            this.form.id = data.id;
            this.form.active = data.active;
            this.form.type = data.type;
            this.form.name = data.name;
            this.form.email = data.email;
            this.form.phone = data.phone;
            this.form.street_1 = data.street_1;
            this.form.street_2 = data.street_2;
            this.form.city = data.city;
            this.form.province = data.province;
            this.form.postal = data.postal;
            this.form.pickup_day = data.pickup_day.split(',');
            this.form.pickup_hours = data.pickup_hours;
            this.form.commission_rate = data.commission_rate;
            this.form.store_code = data.store_code;
            this.showLocationModal = 'edit';
        },

        handleReset() {
            this.mainForm.search = '';
            this.mainForm.sortColumn = 'id';
            this.mainForm.sortDirection = 'asc';
            this.handleSubmit();
        },

        handleSort(sort) {
            this.mainForm.sortColumn = sort.column;
            this.mainForm.sortDirection = sort.direction;
            this.handleSubmit();
        },

        closeLocationModal() {
            this.form.reset();
            this.form.clearErrors();
            this.showLocationModal = false;
            this.showDeleteModal = false;
        },

        submitForm() {
             if (this.showLocationModal === 'add') {
                this.form.post(this.route('settings.location.store'), {
                    onSuccess: () => this.closeLocationModal()
                });

                return true;
            }

            this.form.put(this.route('settings.location.update', this.form.id), {
                onSuccess: () => this.closeLocationModal()
            });
        },

        handleDeleteLocation() {
            this.form.delete(this.route('settings.location.destroy', this.form.id), {
                onSuccess: () => this.closeLocationModal()
            });
        },
    },

    mounted() {
        if (Object.keys(this.types).length === 1) {
            this.form.type = Object.keys(this.types)[0];
        }
    }
}
</script>

<style scoped>
</style>
